import type { IDropDownItem } from "@similarweb/ui-components";
import { Dropdown } from "@similarweb/ui-components";
import {
    StyledDropdownParent,
    StyledSingleSelectDDItem,
    StyledSingleSelectDDItemText,
} from "app/pages/sales-intelligence/common-components/dropdown/styles";
import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";
import useDelayedAction from "app/pages/sales-intelligence/hooks/useDelayedAction";
import useUncontrolledDropdown from "app/pages/sales-intelligence/hooks/useUncontrolledDropdown";
import { buildDropdownSelectedItemsFromArray } from "app/pages/sales-intelligence/utils/dropdowns";
import type { ReactElement } from "react";
import FilterButtonLabel from "../shared/FilterDropdown/FilterButtonLabel/FilterButtonLabel";
import SignalFilterDropdownButton from "app/pages/sales-intelligence/sub-modules/signals/components/filters/shared/FilterDropdown/SignalFilterDropdownButton/SignalFilterDropdownButton";
import { DROPDOWN_AUTO_OPEN_DELAY } from "app/pages/sales-intelligence/sub-modules/signals/constants/dropdown";

type DropdownItem<T> = Omit<IDropDownItem, "id"> & {
    id: T;
};

interface Props<T> {
    selectedValue: T | undefined;
    options: readonly T[];
    autoOpen?: boolean;
    onValueChange(value: T): void;
}

const SignalPeriodsFilter = <T extends string>({
    selectedValue,
    options,
    autoOpen = false,
    onValueChange,
}: Props<T>): ReactElement => {
    const selectedItems: Record<T, boolean> = buildDropdownSelectedItemsFromArray<T>(
        selectedValue ? [selectedValue] : [],
    );

    const { dropdownRef, onUncontrolledOpen } = useUncontrolledDropdown();

    useDelayedAction(DROPDOWN_AUTO_OPEN_DELAY, onUncontrolledOpen, autoOpen);

    const handleClick = (item: DropdownItem<T>): void => {
        onValueChange(item.id);
    };

    return (
        <StyledDropdownParent id="traffic-period-dd">
            <Dropdown
                ref={dropdownRef}
                appendTo="#traffic-period-dd"
                selectedIds={selectedItems}
                width={150}
                onClick={handleClick}
            >
                <SignalFilterDropdownButton
                    id="traffic-period"
                    key="button"
                    hasValue={typeof selectedValue !== "undefined"}
                >
                    {typeof selectedValue !== "undefined" ? (
                        <I18n
                            data-automation="period-dd-button-text"
                            element={FilterButtonLabel}
                            translatable={{
                                key: `si.signals.changing-traction.${selectedValue}.label`,
                            }}
                        />
                    ) : null}
                </SignalFilterDropdownButton>

                {...options.map((option) => (
                    <StyledSingleSelectDDItem
                        id={option}
                        key={option}
                    >
                        <I18n
                            data-automation="period-dd-item-text"
                            element={StyledSingleSelectDDItemText}
                            translatable={{
                                key: `si.signals.changing-traction.${option}.label`,
                            }}
                        />
                    </StyledSingleSelectDDItem>
                ))}
            </Dropdown>
        </StyledDropdownParent>
    );
}

export default SignalPeriodsFilter;
